import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import LanguageSelector from '../../components/common/LanguageSelector';
import TimestampConverter from '../../components/feature/TimestampConverter';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

export default function Timestamp() {
  const { t, i18n } = useTranslation(['timestamp', 'common']);

  // 当前时间戳
  const [currentTimestamp, setCurrentTimestamp] = useState(Date.now());

  // 复制成功提示
  const [copySuccess, setCopySuccess] = useState('');

  // 更新当前时间戳
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimestamp(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // 更新SEO配置 + Schema Markup
  useEffect(() => {
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('timestamp', currentLang);
    updateSEOTags(seoConfig, currentLang);

    // 确保 HTML lang 属性与当前语言一致
    document.documentElement.lang = currentLang;

    // 添加 FAQPage Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": t('page.whatIsTimestamp'),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": t('page.timestampIntro')
          }
        },
        {
          "@type": "Question",
          "name": t('page.whyUseTimestampTitle'),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `${t('page.reason1Title')}: ${t('page.reason1Desc')}. ${t('page.reason2Title')}: ${t('page.reason2Desc')}. ${t('page.reason3Title')}: ${t('page.reason3Desc')}. ${t('page.reason4Title')}: ${t('page.reason4Desc')}.`
          }
        },
        {
          "@type": "Question",
          "name": t('page.year2038Problem'),
          "acceptedAnswer": {
            "@type": "Answer",
            "text": t('page.year2038Content')
          }
        }
      ]
    };

    // 添加 HowTo Schema
    const howToSchema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": t('page.howToUse'),
      "step": [
        {
          "@type": "HowToStep",
          "text": t('page.step1')
        },
        {
          "@type": "HowToStep",
          "text": t('page.step2')
        },
        {
          "@type": "HowToStep",
          "text": t('page.step3')
        },
        {
          "@type": "HowToStep",
          "text": t('page.step4')
        }
      ]
    };

    // 移除旧的 schema 标签（如果存在）
    const oldSchemas = document.querySelectorAll('script[type="application/ld+json"][data-schema]');
    oldSchemas.forEach(schema => schema.remove());

    // 添加 FAQ Schema
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.setAttribute('data-schema', 'faq');
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    // 添加 HowTo Schema
    const howToScript = document.createElement('script');
    howToScript.type = 'application/ld+json';
    howToScript.setAttribute('data-schema', 'howto');
    howToScript.textContent = JSON.stringify(howToSchema);
    document.head.appendChild(howToScript);

    return () => {
      // 清理 schema
      const schemas = document.querySelectorAll('script[type="application/ld+json"][data-schema]');
      schemas.forEach(schema => schema.remove());
    };
  }, [i18n.language, t]);

  // 复制到剪贴板
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(text);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  // 格式化当前时间显示
  const formatCurrentTime = () => {
    const date = new Date(currentTimestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    if (i18n.language === 'zh') {
      return `${year}年${month}月${day}日 ${hours}:${minutes}:${seconds}`;
    } else {
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-6xl mx-auto px-4 py-4 md:py-8">
        {/* 页面标题和说明 */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{t('page.mainTitleSEO')}</h1>
          <p className="text-lg text-gray-600">{t('page.subtitle')}</p>
        </div>

        {/* 1. Interactive Timestamp ↔ Date Converter */}
        <TimestampConverter
          className="mb-6 md:mb-8"
          currentTimestamp={currentTimestamp}
          onCopyToClipboard={copyToClipboard}
          copySuccess={copySuccess}
          formatCurrentTime={formatCurrentTime}
        />

        {/* 2. Current Unix Epoch Time */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">{t('page.currentEpochTime')}</h2>

          <div className="grid md:grid-cols-2 gap-4">
            {/* 秒级时间戳 */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-800">{t('page.secondsOnly')}</h3>
                <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">10 {t('page.digits')}</span>
              </div>
              <div className="relative group">
                <time
                  dateTime={new Date(currentTimestamp).toISOString()}
                  className="font-mono text-2xl md:text-3xl font-bold text-blue-600 mb-2 break-all block"
                >
                  {Math.floor(currentTimestamp / 1000)}
                </time>
                <button
                  onClick={() => copyToClipboard(String(Math.floor(currentTimestamp / 1000)))}
                  className="absolute top-0 right-0 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-sm transition-colors opacity-0 group-hover:opacity-100"
                >
                  <i className="ri-file-copy-line mr-1"></i>
                  {copySuccess === String(Math.floor(currentTimestamp / 1000)) ? t('common:button.copied') : t('common:button.copy')}
                </button>
              </div>
              <p className="text-sm text-gray-600">{formatCurrentTime()}</p>
            </div>

            {/* 毫秒级时间戳 */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-800">{t('page.millisecondsOnly')}</h3>
                <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">13 {t('page.digits')}</span>
              </div>
              <div className="relative group">
                <time
                  dateTime={new Date(currentTimestamp).toISOString()}
                  className="font-mono text-2xl md:text-3xl font-bold text-green-600 mb-2 break-all block"
                >
                  {currentTimestamp}
                </time>
                <button
                  onClick={() => copyToClipboard(String(currentTimestamp))}
                  className="absolute top-0 right-0 bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded text-sm transition-colors opacity-0 group-hover:opacity-100"
                >
                  <i className="ri-file-copy-line mr-1"></i>
                  {copySuccess === String(currentTimestamp) ? t('common:button.copied') : t('common:button.copy')}
                </button>
              </div>
              <p className="text-sm text-gray-600">{formatCurrentTime()}</p>
            </div>
          </div>
        </div>

        {/* Specialized Timestamp Tools - 新增专题工具导航 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3 flex items-center">
            <span className="mr-2">🎯</span>
            {t('page.specializedTools')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('page.specializedToolsDesc')}
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Discord Timestamp Generator */}
            <a
              href="/discord-timestamp-generator"
              className="group block bg-gray-50 hover:bg-gray-100 rounded-xl p-6 border-2 border-gray-200 hover:border-blue-400 transition-all hover:shadow-lg transform hover:-translate-y-1"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <span className="text-2xl">🎮</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-indigo-600 transition-colors">
                    Discord Timestamp Generator
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {t('page.discord.desc')}
                  </p>
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded font-semibold">
                      🔥 {t('page.discord.topSearch')}
                    </span>
                    <span className="text-gray-500">162.5K {t('page.discord.searchesPerMonth')}</span>
                  </div>
                </div>
                <div className="text-indigo-600 group-hover:translate-x-1 transition-transform">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </a>

            {/* YouTube Timestamp Link */}
            <a
              href="/youtube-timestamp-link"
              className="group block bg-gray-50 hover:bg-gray-100 rounded-xl p-6 border-2 border-gray-200 hover:border-blue-400 transition-all hover:shadow-lg transform hover:-translate-y-1"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <span className="text-2xl">📺</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-red-600 transition-colors">
                    YouTube Timestamp Link
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {t('page.youtube.desc')}
                  </p>
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded font-semibold">
                      🎬 {t('page.youtube.popular')}
                    </span>
                    <span className="text-gray-500">5.2K {t('page.discord.searchesPerMonth')}</span>
                  </div>
                </div>
                <div className="text-red-600 group-hover:translate-x-1 transition-transform">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* Programming Language Guides - 编程语言指南 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3 flex items-center">
            <span className="mr-2">💻</span>
            {t('page.programmingGuides')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('page.programmingGuidesDesc')}
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            {/* SQL Timestamp */}
            <a
              href="/sql-timestamp"
              className="group block bg-gray-50 hover:bg-gray-100 rounded-lg p-5 border-2 border-gray-200 hover:border-blue-400 transition-all hover:shadow-lg"
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform">
                  DB
                </div>
                <h3 className="font-bold text-gray-800 group-hover:text-blue-600 transition-colors">
                  SQL
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                {t('page.sql.desc')}
              </p>
              <div className="flex items-center text-xs text-gray-500">
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded mr-2">⭐ {t('page.sql.highPriority')}</span>
                <span>4.6K {t('page.sql.searches')}</span>
              </div>
            </a>

            {/* Python */}
            <a
              href="/python-timestamp"
              className="group block bg-gray-50 hover:bg-gray-100 rounded-lg p-5 border-2 border-gray-200 hover:border-blue-400 transition-all hover:shadow-lg"
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center text-white text-xl group-hover:scale-110 transition-transform">
                  🐍
                </div>
                <h3 className="font-bold text-gray-800 group-hover:text-green-600 transition-colors">
                  Python
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                {t('page.python.desc')}
              </p>
              <div className="flex items-center text-xs text-gray-500">
                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded mr-2">🔥 {t('page.python.popular')}</span>
                <span>2.3K {t('page.sql.searches')}</span>
              </div>
            </a>

            {/* JavaScript */}
            <a
              href="/javascript-timestamp"
              className="group block bg-gray-50 hover:bg-gray-100 rounded-lg p-5 border-2 border-gray-200 hover:border-blue-400 transition-all hover:shadow-lg"
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center text-white text-xl group-hover:scale-110 transition-transform">
                  JS
                </div>
                <h3 className="font-bold text-gray-800 group-hover:text-yellow-600 transition-colors">
                  JavaScript
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                {t('page.javascript.desc')}
              </p>
              <div className="flex items-center text-xs text-gray-500">
                <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded mr-2">⚡ {t('page.javascript.common')}</span>
                <span>2.1K {t('page.sql.searches')}</span>
              </div>
            </a>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 text-center flex items-center justify-center">
              <i className="ri-lightbulb-line text-blue-600 mr-2"></i>
              {t('page.codeExamplesNote')}
            </p>
          </div>
        </div>

        {/* 3. Unix Timestamp Code Cheat Sheet (By Language) */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">{t('page.codeCheatSheet')}</h2>

          {/* H3: How to Get Current Timestamp */}
          <div className="mb-8">
            <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-6 border-b-2 border-blue-200 pb-2">
              {t('page.howToGetTimestamp')}
            </h3>

            <div className="space-y-6">
              {/* JavaScript Get Current Timestamp */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-lg text-sm mr-3">JavaScript</span>
                  {t('page.getCurrentTimestamp')}
                  <span className="text-xs text-gray-500 ml-2">ES6+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => copyToClipboard(`// ${t('page.getMilliseconds')}\nconst timestamp = Date.now();\n\n// ${t('page.getSeconds')}\nconst timestampSec = Math.floor(Date.now() / 1000);`)}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {copySuccess === `// ${t('page.getMilliseconds')}\nconst timestamp = Date.now();\n\n// ${t('page.getSeconds')}\nconst timestampSec = Math.floor(Date.now() / 1000);` ? t('common:button.copied') : t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono">
                    {`// ${t('page.getMilliseconds')}\nconst timestamp = Date.now();\n\n// ${t('page.getSeconds')}\nconst timestampSec = Math.floor(Date.now() / 1000);`}
                  </code>
                </div>
              </div>

              {/* Python Get Current Timestamp */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-lg text-sm mr-3">Python</span>
                  {t('page.getCurrentTimestamp')}
                  <span className="text-xs text-gray-500 ml-2">3.6+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => copyToClipboard(`import time\n\n# ${t('page.getSeconds')}\ntimestamp = int(time.time())\n\n# ${t('page.getMilliseconds')}\ntimestamp_ms = int(time.time() * 1000)`)}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {copySuccess === `import time\n\n# ${t('page.getSeconds')}\ntimestamp = int(time.time())\n\n# ${t('page.getMilliseconds')}\ntimestamp_ms = int(time.time() * 1000)` ? t('common:button.copied') : t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono">
                    {`import time\n\n# ${t('page.getSeconds')}\ntimestamp = int(time.time())\n\n# ${t('page.getMilliseconds')}\ntimestamp_ms = int(time.time() * 1000)`}
                  </code>
                </div>
              </div>

              {/* Java Get Current Timestamp */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-red-100 text-red-800 px-3 py-1 rounded-lg text-sm mr-3">Java</span>
                  {t('page.getCurrentTimestamp')}
                  <span className="text-xs text-gray-500 ml-2">8+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => copyToClipboard(`// ${t('page.getMilliseconds')}\nlong timestamp = System.currentTimeMillis();\n\n// ${t('page.getSeconds')}\nlong timestampSec = System.currentTimeMillis() / 1000;`)}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {copySuccess === `// ${t('page.getMilliseconds')}\nlong timestamp = System.currentTimeMillis();\n\n// ${t('page.getSeconds')}\nlong timestampSec = System.currentTimeMillis() / 1000;` ? t('common:button.copied') : t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono">
                    {`// ${t('page.getMilliseconds')}\nlong timestamp = System.currentTimeMillis();\n\n// ${t('page.getSeconds')}\nlong timestampSec = System.currentTimeMillis() / 1000;`}
                  </code>
                </div>
              </div>

              {/* PHP Get Current Timestamp */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-lg text-sm mr-3">PHP</span>
                  {t('page.getCurrentTimestamp')}
                  <span className="text-xs text-gray-500 ml-2">7.0+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => copyToClipboard(`// ${t('page.getSeconds')}\n$timestamp = time();\n\n// ${t('page.getMilliseconds')}\n$timestamp_ms = round(microtime(true) * 1000);`)}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {copySuccess === `// ${t('page.getSeconds')}\n$timestamp = time();\n\n// ${t('page.getMilliseconds')}\n$timestamp_ms = round(microtime(true) * 1000);` ? t('common:button.copied') : t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono">
                    {`// ${t('page.getSeconds')}\n$timestamp = time();\n\n// ${t('page.getMilliseconds')}\n$timestamp_ms = round(microtime(true) * 1000);`}
                  </code>
                </div>
              </div>
            </div>
          </div>

          {/* H3: Convert Timestamp to Date Object */}
          <div className="mb-8">
            <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-6 border-b-2 border-green-200 pb-2">
              {t('page.convertToDate')}
            </h3>

            <div className="space-y-6">
              {/* JavaScript Timestamp to Date */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-lg text-sm mr-3">JavaScript</span>
                  {t('page.convertTimestampToDate')}
                  <span className="text-xs text-gray-500 ml-2">ES6+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => {
                      const code = `// ${t('page.fromMilliseconds')}\nconst date = new Date(1734598784000);\nconsole.log(date.toLocaleString()); // "12/19/2024, 5:39:44 PM"\n\n// ${t('page.fromSeconds')}\nconst dateSec = new Date(1734598784 * 1000);\nconsole.log(dateSec.toISOString()); // "2024-12-19T09:39:44.000Z"\n\n// ${t('page.formatOptions')}\nconst formatted = date.toLocaleDateString('en-US', {\n  year: 'numeric',\n  month: 'long',\n  day: 'numeric',\n  hour: '2-digit',\n  minute: '2-digit'\n});`;
                      copyToClipboard(code);
                    }}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono whitespace-pre">
                    {`// ${t('page.fromMilliseconds')}\nconst date = new Date(1734598784000);\nconsole.log(date.toLocaleString()); // "12/19/2024, 5:39:44 PM"

// ${t('page.fromSeconds')}
const dateSec = new Date(1734598784 * 1000);
console.log(dateSec.toISOString()); // "2024-12-19T09:39:44.000Z"

// ${t('page.formatOptions')}
const formatted = date.toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});`}
                  </code>
                </div>
              </div>

              {/* Python Timestamp to Datetime */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-lg text-sm mr-3">Python</span>
                  {t('page.convertTimestampToDate')}
                  <span className="text-xs text-gray-500 ml-2">3.6+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => {
                      const code = `from datetime import datetime

# ${t('page.fromSeconds')}
timestamp = 1734598784
dt = datetime.fromtimestamp(timestamp)
print(dt.strftime('%Y-%m-%d %H:%M:%S'))  # "2024-12-19 17:39:44"

# ${t('page.fromMilliseconds')}
timestamp_ms = 1734598784000
dt_ms = datetime.fromtimestamp(timestamp_ms / 1000)

# ${t('page.toUTC')}
dt_utc = datetime.utcfromtimestamp(timestamp)
print(dt_utc.isoformat())  # "2024-12-19T09:39:44"`;
                      copyToClipboard(code);
                    }}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono whitespace-pre">
                    {`from datetime import datetime

# ${t('page.fromSeconds')}
timestamp = 1734598784
dt = datetime.fromtimestamp(timestamp)
print(dt.strftime('%Y-%m-%d %H:%M:%S'))  # "2024-12-19 17:39:44"

# ${t('page.fromMilliseconds')}
timestamp_ms = 1734598784000
dt_ms = datetime.fromtimestamp(timestamp_ms / 1000)

# ${t('page.toUTC')}
dt_utc = datetime.utcfromtimestamp(timestamp)
print(dt_utc.isoformat())  # "2024-12-19T09:39:44"`}
                  </code>
                </div>
              </div>

              {/* Java Timestamp to Date */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-red-100 text-red-800 px-3 py-1 rounded-lg text-sm mr-3">Java</span>
                  {t('page.convertTimestampToDate')}
                  <span className="text-xs text-gray-500 ml-2">8+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => {
                      const code = `import java.util.Date;
import java.text.SimpleDateFormat;

// ${t('page.fromMilliseconds')}
long timestamp = 1734598784000L;
Date date = new Date(timestamp);

// ${t('page.formatDate')}
SimpleDateFormat sdf = new SimpleDateFormat("yyyy-MM-dd HH:mm:ss");
String formatted = sdf.format(date);
System.out.println(formatted);  // "2024-12-19 17:39:44"

// ${t('page.fromSeconds')}
Date dateSec = new Date(1734598784L * 1000);`;
                      copyToClipboard(code);
                    }}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono whitespace-pre">
                    {`import java.util.Date;
import java.text.SimpleDateFormat;

// ${t('page.fromMilliseconds')}
long timestamp = 1734598784000L;
Date date = new Date(timestamp);

// ${t('page.formatDate')}
SimpleDateFormat sdf = new SimpleDateFormat("yyyy-MM-dd HH:mm:ss");
String formatted = sdf.format(date);
System.out.println(formatted);  // "2024-12-19 17:39:44"

// ${t('page.fromSeconds')}
Date dateSec = new Date(1734598784L * 1000);`}
                  </code>
                </div>
              </div>

              {/* PHP Timestamp to Date */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-lg text-sm mr-3">PHP</span>
                  {t('page.convertTimestampToDate')}
                  <span className="text-xs text-gray-500 ml-2">7.0+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => {
                      const code = `<?php
// ${t('page.fromSeconds')}
$timestamp = 1734598784;
$date = date('Y-m-d H:i:s', $timestamp);
echo $date;  // "2024-12-19 17:39:44"

// ${t('page.formatDate')}
$formatted = date('F j, Y g:i A', $timestamp);
echo $formatted;  // "December 19, 2024 5:39 PM"

// ${t('page.fromMilliseconds')}
$timestamp_ms = 1734598784000;
$date_ms = date('Y-m-d H:i:s', $timestamp_ms / 1000);
?>`;
                      copyToClipboard(code);
                    }}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono whitespace-pre">
                    {`<?php
// ${t('page.fromSeconds')}
$timestamp = 1734598784;
$date = date('Y-m-d H:i:s', $timestamp);
echo $date;  // "2024-12-19 17:39:44"

// ${t('page.formatDate')}
$formatted = date('F j, Y g:i A', $timestamp);
echo $formatted;  // "December 19, 2024 5:39 PM"

// ${t('page.fromMilliseconds')}
$timestamp_ms = 1734598784000;
$date_ms = date('Y-m-d H:i:s', $timestamp_ms / 1000);
?>`}
                  </code>
                </div>
              </div>

              {/* MySQL FROM_UNIXTIME() */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-lg text-sm mr-3">MySQL</span>
                  {t('page.convertTimestampToDate')}
                  <span className="text-xs text-gray-500 ml-2">5.7+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => {
                      const code = `-- ${t('page.fromSeconds')}
SELECT FROM_UNIXTIME(1734598784);
-- Result: 2024-12-19 17:39:44

-- ${t('page.formatDate')}
SELECT FROM_UNIXTIME(1734598784, '%Y-%m-%d %H:%i:%s');

-- ${t('page.fromMilliseconds')}
SELECT FROM_UNIXTIME(1734598784000 / 1000);`;
                      copyToClipboard(code);
                    }}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono whitespace-pre">
                    {`-- ${t('page.fromSeconds')}
SELECT FROM_UNIXTIME(1734598784);
-- Result: 2024-12-19 17:39:44

-- ${t('page.formatDate')}
SELECT FROM_UNIXTIME(1734598784, '%Y-%m-%d %H:%i:%s');

-- ${t('page.fromMilliseconds')}
SELECT FROM_UNIXTIME(1734598784000 / 1000);`}
                  </code>
                </div>
              </div>
            </div>
          </div>

          {/* H3: Convert Date to Timestamp */}
          <div>
            <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-6 border-b-2 border-purple-200 pb-2">
              {t('page.convertToTimestamp')}
            </h3>

            <div className="space-y-6">
              {/* JavaScript Date to Timestamp */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-lg text-sm mr-3">JavaScript</span>
                  {t('page.convertDateToTimestamp')}
                  <span className="text-xs text-gray-500 ml-2">ES6+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => {
                      const code = `// ${t('page.fromDateString')}
const date = new Date('2024-12-19T17:39:44');
const timestamp = date.getTime(); // ${t('page.milliseconds')}
const timestampSec = Math.floor(timestamp / 1000); // ${t('page.seconds')}

// ${t('page.fromDateComponents')}
const customDate = new Date(2024, 11, 19, 17, 39, 44); // ${t('page.monthNote')}
const ts = customDate.getTime();

// ${t('page.parseString')}
const parsed = Date.parse('December 19, 2024');`;
                      copyToClipboard(code);
                    }}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono whitespace-pre">
                    {`// ${t('page.fromDateString')}
const date = new Date('2024-12-19T17:39:44');
const timestamp = date.getTime(); // ${t('page.milliseconds')}
const timestampSec = Math.floor(timestamp / 1000); // ${t('page.seconds')}

// ${t('page.fromDateComponents')}
const customDate = new Date(2024, 11, 19, 17, 39, 44); // ${t('page.monthNote')}
const ts = customDate.getTime();

// ${t('page.parseString')}
const parsed = Date.parse('December 19, 2024');`}
                  </code>
                </div>
              </div>

              {/* Python Datetime to Timestamp */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-lg text-sm mr-3">Python</span>
                  {t('page.convertDateToTimestamp')}
                  <span className="text-xs text-gray-500 ml-2">3.6+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => {
                      const code = `from datetime import datetime
import time

# ${t('page.fromDateString')}
dt = datetime.strptime('2024-12-19 17:39:44', '%Y-%m-%d %H:%M:%S')
timestamp = int(dt.timestamp())  # ${t('page.seconds')}
timestamp_ms = int(dt.timestamp() * 1000)  # ${t('page.milliseconds')}

# ${t('page.fromDateComponents')}
dt = datetime(2024, 12, 19, 17, 39, 44)
timestamp = int(time.mktime(dt.timetuple()))

# ${t('page.currentTime')}
now_timestamp = int(datetime.now().timestamp())`;
                      copyToClipboard(code);
                    }}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono whitespace-pre">
                    {`from datetime import datetime
import time

# ${t('page.fromDateString')}
dt = datetime.strptime('2024-12-19 17:39:44', '%Y-%m-%d %H:%M:%S')
timestamp = int(dt.timestamp())  # ${t('page.seconds')}
timestamp_ms = int(dt.timestamp() * 1000)  # ${t('page.milliseconds')}

# ${t('page.fromDateComponents')}
dt = datetime(2024, 12, 19, 17, 39, 44)
timestamp = int(time.mktime(dt.timetuple()))

# ${t('page.currentTime')}
now_timestamp = int(datetime.now().timestamp())`}
                  </code>
                </div>
              </div>

              {/* Java Date to Timestamp */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-red-100 text-red-800 px-3 py-1 rounded-lg text-sm mr-3">Java</span>
                  {t('page.convertDateToTimestamp')}
                  <span className="text-xs text-gray-500 ml-2">8+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => {
                      const code = `import java.util.Date;
import java.text.SimpleDateFormat;

// ${t('page.fromDateString')}
SimpleDateFormat sdf = new SimpleDateFormat("yyyy-MM-dd HH:mm:ss");
Date date = sdf.parse("2024-12-19 17:39:44");
long timestamp = date.getTime(); // ${t('page.milliseconds')}
long timestampSec = timestamp / 1000; // ${t('page.seconds')}

// ${t('page.useCalendar')}
Calendar cal = Calendar.getInstance();
cal.set(2024, Calendar.DECEMBER, 19, 17, 39, 44);
long ts = cal.getTimeInMillis();`;
                      copyToClipboard(code);
                    }}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono whitespace-pre">
                    {`import java.util.Date;
import java.text.SimpleDateFormat;

// ${t('page.fromDateString')}
SimpleDateFormat sdf = new SimpleDateFormat("yyyy-MM-dd HH:mm:ss");
Date date = sdf.parse("2024-12-19 17:39:44");
long timestamp = date.getTime(); // ${t('page.milliseconds')}
long timestampSec = timestamp / 1000; // ${t('page.seconds')}

// ${t('page.useCalendar')}
Calendar cal = Calendar.getInstance();
cal.set(2024, Calendar.DECEMBER, 19, 17, 39, 44);
long ts = cal.getTimeInMillis();`}
                  </code>
                </div>
              </div>

              {/* PHP Date to Timestamp */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-lg text-sm mr-3">PHP</span>
                  {t('page.convertDateToTimestamp')}
                  <span className="text-xs text-gray-500 ml-2">7.0+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => {
                      const code = `<?php
// ${t('page.fromDateString')}
$date = '2024-12-19 17:39:44';
$timestamp = strtotime($date); // ${t('page.seconds')}

// ${t('page.parseFormat')}
$dt = DateTime::createFromFormat('Y-m-d H:i:s', '2024-12-19 17:39:44');
$timestamp = $dt->getTimestamp();

// ${t('page.fromComponents')}
$timestamp = mktime(17, 39, 44, 12, 19, 2024);

// ${t('page.getMilliseconds')}
$timestamp_ms = round(microtime(true) * 1000);
?>`;
                      copyToClipboard(code);
                    }}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono whitespace-pre">
                    {`<?php
// ${t('page.fromDateString')}
$date = '2024-12-19 17:39:44';
$timestamp = strtotime($date); // ${t('page.seconds')}

// ${t('page.parseFormat')}
$dt = DateTime::createFromFormat('Y-m-d H:i:s', '2024-12-19 17:39:44');
$timestamp = $dt->getTimestamp();

// ${t('page.fromComponents')}
$timestamp = mktime(17, 39, 44, 12, 19, 2024);

// ${t('page.getMilliseconds')}
$timestamp_ms = round(microtime(true) * 1000);
?>`}
                  </code>
                </div>
              </div>

              {/* MySQL UNIX_TIMESTAMP() */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-lg text-sm mr-3">MySQL</span>
                  {t('page.convertDateToTimestamp')}
                  <span className="text-xs text-gray-500 ml-2">5.7+</span>
                </h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
                  <button
                    onClick={() => {
                      const code = `-- ${t('page.fromDateString')}
SELECT UNIX_TIMESTAMP('2024-12-19 17:39:44');
-- Result: 1734598784

-- ${t('page.currentTime')}
SELECT UNIX_TIMESTAMP();

-- ${t('page.fromDatetime')}
SELECT UNIX_TIMESTAMP(NOW());`;
                      copyToClipboard(code);
                    }}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    {t('common:button.copy')}
                  </button>
                  <code className="text-green-400 text-sm font-mono whitespace-pre">
                    {`-- ${t('page.fromDateString')}
SELECT UNIX_TIMESTAMP('2024-12-19 17:39:44');
-- Result: 1734598784

-- ${t('page.currentTime')}
SELECT UNIX_TIMESTAMP();

-- ${t('page.fromDatetime')}
SELECT UNIX_TIMESTAMP(NOW());`}
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Frequently Asked Questions (FAQ) */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">{t('page.faq')}</h2>

          {/* FAQ Item 1: What is a Unix Timestamp? */}
          <div className="mb-8">
            <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">{t('page.whatIsTimestamp')}</h3>

            <div className="prose prose-blue max-w-none">
              <div className="space-y-4 text-gray-600">
                <p className="text-base leading-relaxed">{t('page.timestampIntro')}</p>

                <div className="bg-blue-50 rounded-xl p-4 my-4">
                  <div className="flex items-start space-x-3">
                    <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">{t('page.unixEpochTitle')}</h4>
                      <p className="text-sm">{t('page.unixEpochDesc')}</p>
                    </div>
                  </div>
                </div>

                <h4 className="text-lg font-semibold text-gray-800 mt-6 mb-3">{t('page.timestampFormatsTitle')}</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span><strong>{t('page.secondsFormat')}:</strong> {t('page.secondsFormatDesc')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span><strong>{t('page.millisecondsFormat')}:</strong> {t('page.millisecondsFormatDesc')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* FAQ Item 2: Why do we use Unix Time? */}
          <div className="mb-8">
            <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">{t('page.whyUseTimestampTitle')}</h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-global-line text-blue-600 text-xl"></i>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-1">{t('page.reason1Title')}</h5>
                    <p className="text-sm text-gray-600">{t('page.reason1Desc')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-calculator-line text-blue-600 text-xl"></i>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-1">{t('page.reason2Title')}</h5>
                    <p className="text-sm text-gray-600">{t('page.reason2Desc')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-database-line text-blue-600 text-xl"></i>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-1">{t('page.reason3Title')}</h5>
                    <p className="text-sm text-gray-600">{t('page.reason3Desc')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-sort-asc text-blue-600 text-xl"></i>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-1">{t('page.reason4Title')}</h5>
                    <p className="text-sm text-gray-600">{t('page.reason4Desc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Item 3: The Year 2038 Problem */}
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">{t('page.year2038Problem')}</h3>

            <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
              <div className="flex items-start space-x-3">
                <i className="ri-alert-line text-orange-600 text-2xl mt-0.5"></i>
                <div>
                  <p className="text-base text-gray-700 leading-relaxed">{t('page.year2038Content')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 5. How to Use */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('page.howToUse')}</h2>
          <div className="space-y-3 md:space-y-4 text-gray-600">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">1</div>
              <p className="text-sm md:text-base">{t('page.step1')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">2</div>
              <p className="text-sm md:text-base">{t('page.step2')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">3</div>
              <p className="text-sm md:text-base">{t('page.step3')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">4</div>
              <p className="text-sm md:text-base">{t('page.step4')}</p>
            </div>
          </div>
        </div>

        {/* 6. Related Tools */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">{t('page.relatedTools')}</h2>
          <p className="text-gray-600 mb-6">{t('page.relatedToolsDesc')}</p>

          <div className="grid md:grid-cols-3 gap-6">
            <a href="/timer" className="group block bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-xl p-6 border border-blue-200 transition-all hover:shadow-lg">
              <div className="flex items-center mb-3">
                <i className="ri-timer-line text-3xl text-blue-600 mr-3"></i>
                <h3 className="text-lg font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">{t('page.timerTool')}</h3>
              </div>
              <p className="text-sm text-gray-600">{t('page.timerToolDesc')}</p>
            </a>

            <a href="/stopwatch" className="group block bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 rounded-xl p-6 border border-green-200 transition-all hover:shadow-lg">
              <div className="flex items-center mb-3">
                <i className="ri-play-circle-line text-3xl text-green-600 mr-3"></i>
                <h3 className="text-lg font-semibold text-gray-800 group-hover:text-green-600 transition-colors">{t('page.stopwatchTool')}</h3>
              </div>
              <p className="text-sm text-gray-600">{t('page.stopwatchToolDesc')}</p>
            </a>

            <a href="/worldclock" className="group block bg-gradient-to-br from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-xl p-6 border border-purple-200 transition-all hover:shadow-lg">
              <div className="flex items-center mb-3">
                <i className="ri-global-line text-3xl text-purple-600 mr-3"></i>
                <h3 className="text-lg font-semibold text-gray-800 group-hover:text-purple-600 transition-colors">{t('page.worldClockTool')}</h3>
              </div>
              <p className="text-sm text-gray-600">{t('page.worldClockToolDesc')}</p>
            </a>
          </div>
        </div>

        {/* 7. Footer */}
        <footer className="bg-gray-100 text-gray-800 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col lg:flex-row justify-between items-start space-y-6 lg:space-y-0">
            {/* Left side - Website description */}
            <div className="lg:w-2/3">
              <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('common:footer.about')}</h4>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {t('common:footer.aboutDesc')}
              </p>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-3">
                  <i className="ri-timer-line text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.timer')}</span> - {t('common:footer.timerDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-play-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.stopwatch')}</span> - {t('common:footer.stopwatchDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-global-line text-purple-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.worldclock')}</span> - {t('common:footer.worldClockDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-time-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.timestamp')}</span> - {t('common:footer.timestampDesc')}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <a href="/terms" className="hover:text-gray-700 transition-colors">{t('common:footer.termsOfService')}</a>
                  <span>|</span>
                  <a href="#" className="hover:text-gray-700 transition-colors">{t('common:footer.privacy')}</a>
                  <span>|</span>
                  <a href="mailto:mu9347mu@gmail.com?subject=Contact Us - timer.today&body=Hello timer.today team,%0D%0A%0D%0AI would like to..." className="hover:text-gray-700 transition-colors">{t('common:footer.contactUs')}</a>
                  <span>|</span>
                  <span>{t('common:footer.copyright')}</span>
                  <span className="lg:hidden">|</span>
                  <div className="lg:hidden">
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </div>

            {/* Vertical divider - only visible on large screens */}
            <div className="hidden lg:flex w-px bg-gray-300 mx-6 h-40 self-center"></div>

            {/* Right side - Multilingual content - only show on desktop */}
            <div className="hidden lg:block lg:w-1/3">
              <div className="space-y-3 text-sm text-gray-600 mt-12">
                <p>timestamp converter unix time epoch</p>
                <p>时间戳转换 Unix时间 纪元时间</p>
                <p>タイムスタンプ変換 Unix時間</p>
                <p>convertidor timestamp tiempo unix</p>
                <p>Zeitstempel Konverter Unix Zeit</p>
                <LanguageSelector />
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
